"use client";

import { Icons } from "@/components/icons";
import { STEPS, STATS, type StatItem } from "../_data/page-data";
import {
  AnimatedSection,
  AnimatedCounter,
  StaggerGrid,
  FadeInChild,
} from "./motion";

/* ─── Single stat cell ─── */
function StatCell({ stat }: { stat: StatItem }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
        {stat.numericValue != null ? (
          <AnimatedCounter
            target={stat.numericValue}
            suffix={stat.suffix}
            duration={1.8}
          />
        ) : (
          stat.value
        )}
      </span>
      <span className="mt-1 text-sm font-semibold text-slate-700">{stat.label}</span>
      <span className="mt-0.5 text-xs text-slate-400">{stat.sub}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   How It Works Section
   ═══════════════════════════════════════════════ */

export function HowItWorksSection() {
  return (
    <AnimatedSection className="container py-16 md:py-24 lg:py-32">
      <div className="mx-auto flex max-w-[58rem] flex-col items-center space-y-4 text-center mb-12">
        <span className="inline-flex items-center rounded-full bg-cyan-50 px-4 py-1 text-xs font-semibold text-cyan-600 ring-1 ring-inset ring-cyan-200">
          How It Works
        </span>
        <h2 className="font-heading text-3xl leading-[1.1] sm:text-3xl md:text-6xl">
          Live in <span className="text-gradient">three steps</span>
        </h2>
      </div>

      <StaggerGrid className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
        {STEPS.map((s, idx) => {
          const Ic = Icons[s.iconKey as keyof typeof Icons];
          return (
            <FadeInChild key={s.step}>
              <div className="relative flex flex-col items-center text-center group">
                {/* Connecting line (visible on md+) */}
                {idx < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-7 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-px bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200" />
                )}
                <div
                  className={`flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${s.gradient} shadow-lg transition-transform group-hover:scale-110`}
                >
                  <Ic className="h-5 w-5 text-white" />
                </div>
                <span className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-300">
                  Step {s.step}
                </span>
                <h3 className="mt-2 text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            </FadeInChild>
          );
        })}
      </StaggerGrid>
    </AnimatedSection>
  );
}

/* ═══════════════════════════════════════════════
   Stats Section
   ═══════════════════════════════════════════════ */

export function StatsSection() {
  return (
    <AnimatedSection className="container py-16">
      <StaggerGrid className="mx-auto grid max-w-4xl grid-cols-2 gap-6 md:grid-cols-4">
        {STATS.map((s) => (
          <FadeInChild key={s.label}>
            <StatCell stat={s} />
          </FadeInChild>
        ))}
      </StaggerGrid>
    </AnimatedSection>
  );
}
